'use client';

import { createContext, useContext } from 'react';
import { FunctionHelpers } from '@headless-workspace/utils';

export type LayoutHeaderOffsetContextType = {
    /**
     * Header offset in pixels
     */
    headerOffset: number;
    setHeaderOffset: (offset: number) => void;

    /**
     * Full Header height in pixels
     */
    headerHeight: number;
    setHeaderHeight: (height: number) => void;

    /**
     * PDP Fixed AddToBasket visibility
     */
    fixedAddToBasketVisible: boolean;
    setFixedAddToBasketVisible: (visible: boolean) => void;

    /**
     * PDP Fixed AddToBasket height
     */
    fixedAddToBasketHeight: number;
    setAddToBasketHeight: (height: number) => void;

    /**
     * BackToTop visibility
     */
    backToTopVisible: boolean;
    setBackToTopVisible: (visible: boolean) => void;
};

export type LayoutStateContextType = {
    header: {
        headerOffset: LayoutHeaderOffsetContextType['headerOffset'];
        headerHeight: LayoutHeaderOffsetContextType['headerHeight'];
    };
    fixedAddToBasket: {
        fixedAddToBasketVisible: LayoutHeaderOffsetContextType['fixedAddToBasketVisible'];
        fixedAddToBasketHeight: LayoutHeaderOffsetContextType['fixedAddToBasketHeight'];
    };
};

export type LayoutActionContextType = {
    header: {
        setHeaderOffset: LayoutHeaderOffsetContextType['setHeaderOffset'];
        setHeaderHeight: LayoutHeaderOffsetContextType['setHeaderHeight'];
    };
    fixedAddToBasket: {
        setFixedAddToBasketVisible: LayoutHeaderOffsetContextType['setFixedAddToBasketVisible'];
        setFixedAddToBasketHeight: LayoutHeaderOffsetContextType['setAddToBasketHeight'];
    };
};

const layoutStateContextInitialState: LayoutStateContextType = {
    header: {
        headerOffset: 0,
        headerHeight: 0,
    },
    fixedAddToBasket: {
        fixedAddToBasketVisible: false,
        fixedAddToBasketHeight: 0,
    },
};

const layoutActionContextInitialState: LayoutActionContextType = {
    header: {
        setHeaderOffset: FunctionHelpers.emptyFn,
        setHeaderHeight: FunctionHelpers.emptyFn,
    },
    fixedAddToBasket: {
        setFixedAddToBasketVisible: FunctionHelpers.emptyFn,
        setFixedAddToBasketHeight: FunctionHelpers.emptyFn,
    },
};

// Context for UI elements that are outside the main tag,
// but visible in the viewport (for instance header, footer, ...)
export const LayoutStateContext = createContext<LayoutStateContextType>(layoutStateContextInitialState);
export const LayoutActionContext = createContext<LayoutActionContextType>(layoutActionContextInitialState);

export const useLayoutStateContext = () => useContext(LayoutStateContext);
export const useLayoutActionContext = () => useContext(LayoutActionContext);
